import React from 'react';
import { MicIcon, SendIcon, CloseIcon, StopIcon, PlayArrowIcon, PauseIcon, DeleteIcon } from '@/components/icons';
import { Box, CircularProgress, IconButton, Typography, useTheme } from '@mui/material';
import { scaledFontSize } from './createWidgetTheme';
import { useWidgetBrandColors } from './useWidgetBrandColors';
import { useWidgetI18n } from '../i18n/WidgetI18nProvider';

export type VoiceRecorderState = 'idle' | 'recording' | 'review' | 'transcribing' | 'error';

export type VoiceRecorderErrorReason =
  | 'mic_denied'
  | 'mic_unsupported'
  | 'mic_failure'
  | 'playback_failed';

interface VoiceRecorderProps {
  /**
   * Called when the user confirms a recording — either from the review state
   * (stop → review → send) or directly from the recording state (one-tap
   * send). The recorder transitions to `'transcribing'` immediately before
   * invoking this. The parent owns the upload + chat send pipeline.
   */
  onRecordingComplete: (audioBlob: Blob) => void;
  /**
   * Optional callback invoked when getUserMedia or the MediaRecorder
   * surface fails. The parent uses this to render a `WidgetNotice` banner
   * (mic blocked / network error) above the input bar.
   */
  onError?: (reason: VoiceRecorderErrorReason) => void;
  disabled?: boolean;
  /** When true, Send buttons are disabled but recording/review still work. */
  sendDisabled?: boolean;
  recorderState: VoiceRecorderState;
  setRecorderState: (state: VoiceRecorderState) => void;
}

const MAX_DURATION_MS = 120_000;
const MAX_DURATION_SECONDS = MAX_DURATION_MS / 1000;
const WAVEFORM_BARS = 24;

function negotiateMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return undefined;
  }

  // Order matters: mp4 first because Safari is the only major target that
  // can neither record nor decode webm. Listing mp4 first means we end up
  // with a container the review <audio> element can actually play back on
  // every browser that supports MediaRecorder at all.
  const candidates = [
    'audio/mp4',
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
  ];
  for (const mime of candidates) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return undefined;
}

function formatTime(seconds: number) {
  const safe = Math.max(0, Math.floor(seconds));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function VoiceRecorder({
  onRecordingComplete,
  onError,
  disabled,
  sendDisabled,
  recorderState,
  setRecorderState,
}: VoiceRecorderProps) {
  const theme = useTheme();
  const { t, locale } = useWidgetI18n();
  const { primaryOnPaper } = useWidgetBrandColors();
  const widgetScale = (theme as typeof theme & { widgetTypographyScale?: number }).widgetTypographyScale ?? 1;
  const hasScaledTypography = widgetScale !== 1;

  const mediaRecorderRef = React.useRef<MediaRecorder | null>(null);
  const chunksRef = React.useRef<Blob[]>([]);
  const timerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const maxDurationTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = React.useRef(false);
  const sendOnStopRef = React.useRef(false);
  const [elapsed, setElapsed] = React.useState(0);
  const elapsedRef = React.useRef(0);

  // Review state local data: the captured blob, an object URL for the
  // <audio> element to play back, the playhead position, and whether the
  // audio is currently playing. We deliberately keep these out of the
  // parent so that re-renders driven by the chat history don't invalidate
  // the audio element or its playback state.
  const [reviewBlob, setReviewBlob] = React.useState<Blob | null>(null);
  const [reviewBlobUrl, setReviewBlobUrl] = React.useState<string | null>(null);
  const [reviewDurationSec, setReviewDurationSec] = React.useState(0);
  const [reviewCurrentSec, setReviewCurrentSec] = React.useState(0);
  const [isPlaying, setIsPlaying] = React.useState(false);
  // Tracks the <audio>'s `canplay` event. The blob URL is created the
  // same tick we enter 'review', so the very first tap on Play almost
  // always lands before the element has decoded enough to play. We use
  // this to (a) gate the button's appearance and (b) defer play() until
  // the element is actually ready.
  const [audioReady, setAudioReady] = React.useState(false);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);

  const clearTimers = React.useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (maxDurationTimerRef.current) clearTimeout(maxDurationTimerRef.current);
    timerRef.current = null;
    maxDurationTimerRef.current = null;
  }, []);

  const releaseReviewBlob = React.useCallback(() => {
    if (reviewBlobUrl) {
      try { URL.revokeObjectURL(reviewBlobUrl); } catch { /* noop */ }
    }
    setReviewBlobUrl(null);
    setReviewBlob(null);
    setReviewDurationSec(0);
    setReviewCurrentSec(0);
    setIsPlaying(false);
    setAudioReady(false);
  }, [reviewBlobUrl]);

  // Pure side-effect: stop/release the MediaRecorder + its mic stream.
  // Used by both `cancelRecording` (user-initiated) and the unmount
  // cleanup. Crucially this does NOT touch parent state — that would
  // race with state updates the parent has already enqueued (e.g. a
  // setState('recording') that fired one tick before this unmount).
  const teardownMediaRecorder = React.useCallback(() => {
    cancelledRef.current = true;
    clearTimers();
    const mr = mediaRecorderRef.current;
    if (mr) {
      mr.ondataavailable = null;
      mr.onstop = null;
      mr.onerror = null;
      if (mr.state !== 'inactive') {
        mr.stream.getTracks().forEach(t => t.stop());
        try { mr.stop(); } catch { /* already inactive */ }
      }
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
  }, [clearTimers]);

  const cancelRecording = React.useCallback(() => {
    teardownMediaRecorder();
    setElapsed(0);
    elapsedRef.current = 0;
    releaseReviewBlob();
    setRecorderState('idle');
  }, [teardownMediaRecorder, releaseReviewBlob, setRecorderState]);

  const stopRecording = React.useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state === 'recording') {
      mr.stop();
    }
  }, []);

  const sendRecording = React.useCallback(() => {
    sendOnStopRef.current = true;
    const mr = mediaRecorderRef.current;
    if (mr && mr.state === 'recording') {
      mr.stream.getTracks().forEach(t => t.stop());
      mr.stop();
    }
  }, []);

  const startRecording = React.useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setRecorderState('error');
      onError?.('mic_unsupported');
      return;
    }

    try {
      cancelledRef.current = false;
      releaseReviewBlob();
      setElapsed(0);
      elapsedRef.current = 0;
      const mimeType = negotiateMimeType();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];

      mr.ondataavailable = (e) => {
        if (e.data.size > 0 && !cancelledRef.current) {
          chunksRef.current.push(e.data);
        }
      };

      mr.onstop = () => {
        clearTimers();
        stream.getTracks().forEach(t => t.stop());

        if (cancelledRef.current) {
          chunksRef.current = [];
          return;
        }

        const blob = new Blob(chunksRef.current, { type: mimeType || mr.mimeType || 'audio/webm' });
        chunksRef.current = [];

        if (sendOnStopRef.current) {
          sendOnStopRef.current = false;
          if (blob.size > 0) {
            setRecorderState('transcribing');
            onRecordingComplete(blob);
          } else {
            setRecorderState('idle');
          }
        } else if (blob.size > 0) {
          const url = URL.createObjectURL(blob);
          setReviewBlob(blob);
          setReviewBlobUrl(url);
          setReviewDurationSec(elapsedRef.current);
          setReviewCurrentSec(0);
          setIsPlaying(false);
          setAudioReady(false);
          setRecorderState('review');
        } else {
          setRecorderState('idle');
        }
      };

      mr.onerror = () => {
        cancelRecording();
        setRecorderState('error');
        onError?.('mic_failure');
      };

      mr.start(250);
      setRecorderState('recording');

      timerRef.current = setInterval(() => {
        setElapsed(prev => {
          const next = prev + 1;
          elapsedRef.current = next;
          return next;
        });
      }, 1000);

      maxDurationTimerRef.current = setTimeout(() => {
        stopRecording();
      }, MAX_DURATION_MS);

    } catch (err) {
      // Most commonly DOMException with `NotAllowedError` (user blocked
      // the mic) or `NotFoundError` (no input device). Both surface as
      // `mic_denied` for the banner because the remediation is the same:
      // "allow access in browser settings".
      setRecorderState('error');
      const reason: VoiceRecorderErrorReason =
        err instanceof Error &&
        (err.name === 'NotAllowedError' || err.name === 'SecurityError' || err.name === 'PermissionDeniedError')
          ? 'mic_denied'
          : 'mic_failure';
      onError?.(reason);
    }
  }, [
    onError,
    onRecordingComplete,
    cancelRecording,
    clearTimers,
    releaseReviewBlob,
    stopRecording,
    setRecorderState,
  ]);

  // Only release the mic + timers on unmount. Do NOT call cancelRecording
  // here: cancelRecording flips parent state to 'idle', which would race
  // with the parent's own state ('recording' / 'review' / 'transcribing')
  // and would reset the UI mid-flow if this component were ever to remount.
  React.useEffect(() => {
    return () => {
      teardownMediaRecorder();
    };
  }, [teardownMediaRecorder]);

  React.useEffect(() => {
    return () => {
      if (reviewBlobUrl) {
        try { URL.revokeObjectURL(reviewBlobUrl); } catch { /* noop */ }
      }
    };
  }, [reviewBlobUrl]);

  // Show "Preparing recording…" for a fixed duration so the browser has
  // time to decode the blob before the user can tap Play.
  const PREPARE_DURATION_MS = 5_000;

  React.useEffect(() => {
    if (!reviewBlobUrl) return;
    const audio = audioRef.current;
    if (audio) { try { audio.load(); } catch { /* noop */ } }

    const id = setTimeout(() => setAudioReady(true), PREPARE_DURATION_MS);
    return () => clearTimeout(id);
  }, [reviewBlobUrl]);

  // ── Review playback wiring ───────────────────────────────────────────────
  // By the time the play button is visible, the 7-second prepare timer
  // has elapsed and the blob should be fully decoded. Just call play()
  // directly and surface any rejection via onError.
  const togglePlayback = React.useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => {
          setIsPlaying(false);
          onError?.('playback_failed');
        });
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  }, [onError]);

  const discardReview = React.useCallback(() => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
      } catch { /* noop */ }
    }
    releaseReviewBlob();
    setRecorderState('idle');
  }, [releaseReviewBlob, setRecorderState]);

  const sendReview = React.useCallback(() => {
    if (!reviewBlob) return;
    if (audioRef.current) {
      try {
        audioRef.current.pause();
      } catch { /* noop */ }
    }
    setRecorderState('transcribing');
    onRecordingComplete(reviewBlob);
    // We intentionally do NOT release the object URL or blob here — the
    // parent may need to re-send the same blob if transcription fails.
  }, [onRecordingComplete, reviewBlob, setRecorderState]);

  // ── RECORDING UI ─────────────────────────────────────────────────────────
  if (recorderState === 'recording') {
    const remainingSec = Math.max(0, MAX_DURATION_SECONDS - elapsed);
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 1.25,
          py: 0.5,
          flex: 1,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: theme.palette.error.main,
            flexShrink: 0,
          }}
        />

        <Typography
          variant="body2"
          lang={locale}
          aria-label={t('voice.elapsedRecordingTime')}
          sx={{
            fontVariantNumeric: 'tabular-nums',
            minWidth: 42,
            color: theme.palette.error.main,
            fontWeight: 700,
            flexShrink: 0,
            ...(hasScaledTypography && { fontSize: scaledFontSize(theme, 16) }),
          }}
        >
          {formatTime(elapsed)}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: 24,
            flex: 1,
            minWidth: 0,
          }}
        >
          {Array.from({ length: WAVEFORM_BARS }).map((_, i) => (
            <Box
              key={i}
              sx={{
                width: 2.5,
                borderRadius: 1,
                bgcolor: theme.palette.error.main,
                opacity: 0.7,
                flexShrink: 0,
                animation: `waveform-bar-${i % 5} 1s ease-in-out ${i * 0.06}s infinite alternate`,
                [`@keyframes waveform-bar-${i % 5}`]: {
                  '0%': { height: 3 + (i % 3) },
                  '50%': { height: 8 + ((i * 7) % 12) },
                  '100%': { height: 4 + ((i * 3) % 8) },
                },
              }}
            />
          ))}
        </Box>

        <Typography
          variant="body2"
          lang={locale}
          aria-label={t('voice.timeRemaining')}
          sx={{
            fontVariantNumeric: 'tabular-nums',
            minWidth: 42,
            color: theme.palette.text.secondary,
            flexShrink: 0,
            ...(hasScaledTypography && { fontSize: scaledFontSize(theme, 16) }),
          }}
        >
          {formatTime(remainingSec)}
        </Typography>

        <IconButton
          onClick={cancelRecording}
          size="small"
          lang={locale}
          aria-label={t('voice.cancelRecording')}
          title={t('voice.cancelTitle')}
          sx={{
            width: 46,
            height: 46,
            bgcolor: theme.palette.grey[100],
            color: theme.palette.text.secondary,
            flexShrink: 0,
            '&:hover': { bgcolor: theme.palette.grey[200], color: theme.palette.error.main },
          }}
        >
          <CloseIcon sx={{ fontSize: 24 }} aria-hidden />
        </IconButton>

        <IconButton
          onClick={stopRecording}
          size="small"
          lang={locale}
          aria-label={t('voice.stopRecording')}
          title={t('voice.stopTitle')}
          sx={{
            bgcolor: theme.palette.error.main,
            color: theme.palette.error.contrastText,
            width: 46,
            height: 46,
            flexShrink: 0,
            '&:hover': { bgcolor: theme.palette.error.dark },
            transition: 'background-color 0.15s',
          }}
        >
          <StopIcon sx={{ fontSize: 22 }} aria-hidden />
        </IconButton>

        <IconButton
          onClick={sendRecording}
          disabled={sendDisabled}
          size="small"
          lang={locale}
          aria-label={t('voice.sendVoiceMessage')}
          title={
            sendDisabled
              ? t('voice.waitingForResponseTitle')
              : t('voice.sendTitle')
          }
          sx={{
            bgcolor: sendDisabled ? theme.palette.action.disabledBackground : theme.palette.primary.main,
            color: sendDisabled ? theme.palette.action.disabled : theme.palette.primary.contrastText,
            width: 46,
            height: 46,
            flexShrink: 0,
            '&:hover': sendDisabled ? {} : { bgcolor: theme.palette.primary.dark },
            transition: 'background-color 0.15s',
          }}
        >
          <SendIcon sx={{ fontSize: 24 }} aria-hidden />
        </IconButton>
      </Box>
    );
  }

  // ── REVIEW UI ────────────────────────────────────────────────────────────
  if (recorderState === 'review' && reviewBlobUrl) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 0.75,
          py: 0.5,
          flex: 1,
          minWidth: 0,
        }}
        aria-busy={!audioReady}
      >
        {/*
          While the just-recorded blob is still decoding we render a
          minimal loading shell instead of the full playback row. The
          <audio> element below stays mounted in BOTH branches so it can
          fire `canplay` and flip `audioReady` true - if we hid it while
          loading, the decode would never start and the spinner would
          spin forever.
        */}
        {!audioReady ? (
          <Box
            role="status"
            aria-labelledby="voice-preparing-status-label"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flex: 1,
              minWidth: 0,
              py: 0.75,
            }}
          >
            <CircularProgress
              size={22}
              thickness={4}
              sx={{ color: primaryOnPaper, flexShrink: 0 }}
              aria-hidden
            />
            <Typography
              id="voice-preparing-status-label"
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                ...(hasScaledTypography && { fontSize: scaledFontSize(theme, 14) }),
              }}
            >
              Preparing recording...
            </Typography>
            <Box sx={{ flex: 1 }} />
            <IconButton
              onClick={discardReview}
              size="small"
              lang={locale}
              aria-label={t('voice.discardRecording')}
              title={t('voice.discardTitle')}
              sx={{
                width: 40,
                height: 40,
                color: theme.palette.text.secondary,
                flexShrink: 0,
                '&:hover': { color: theme.palette.error.main },
              }}
            >
              <DeleteIcon sx={{ fontSize: 22 }} aria-hidden />
            </IconButton>
          </Box>
        ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: 1, minWidth: 0 }}>
        <IconButton
          onClick={togglePlayback}
          size="small"
          lang={locale}
          aria-label={
            isPlaying ? t('voice.pausePlayback') : t('voice.playRecording')
          }
          title={isPlaying ? t('voice.pauseTitle') : t('voice.playTitle')}
          sx={{
            width: 50,
            height: 50,
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            flexShrink: 0,
            '&:hover': { bgcolor: theme.palette.primary.dark },
          }}
        >
          {isPlaying ? (
            <PauseIcon sx={{ fontSize: 28 }} aria-hidden />
          ) : (
            <PlayArrowIcon sx={{ fontSize: 30 }} aria-hidden />
          )}
        </IconButton>

        <Box sx={{ flex: 1, minWidth: 0, height: 34, display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              height: 30,
            }}
          >
            {Array.from({ length: WAVEFORM_BARS }).map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: 4,
                  height: 8 + ((i * 7) % 20),
                  borderRadius: 2,
                  bgcolor: theme.palette.grey[300],
                  flexShrink: 0,
                }}
              />
            ))}
          </Box>
        </Box>

        <Typography
          variant="body2"
          sx={{
            fontVariantNumeric: 'tabular-nums',
            minWidth: 78,
            color: theme.palette.text.secondary,
            flexShrink: 0,
            ...(hasScaledTypography && { fontSize: scaledFontSize(theme, 16) }),
          }}
        >
          {formatTime(reviewCurrentSec)}/{formatTime(reviewDurationSec)}
        </Typography>

        <IconButton
          onClick={discardReview}
          size="small"
          lang={locale}
          aria-label={t('voice.discardRecording')}
          title={t('voice.discardTitle')}
          sx={{
            width: 40,
            height: 40,
            color: theme.palette.text.secondary,
            flexShrink: 0,
            '&:hover': { color: theme.palette.error.main },
          }}
        >
          <DeleteIcon sx={{ fontSize: 22 }} aria-hidden />
        </IconButton>

        <IconButton
          onClick={sendReview}
          disabled={sendDisabled}
          size="small"
          lang={locale}
          aria-label={t('voice.sendVoiceMessage')}
          title={
            sendDisabled
              ? t('voice.waitingForResponseTitle')
              : t('voice.sendTitle')
          }
          sx={{
            bgcolor: sendDisabled ? theme.palette.action.disabledBackground : theme.palette.primary.main,
            color: sendDisabled ? theme.palette.action.disabled : theme.palette.primary.contrastText,
            width: 50,
            height: 50,
            flexShrink: 0,
            '&:hover': sendDisabled ? {} : { bgcolor: theme.palette.primary.dark },
            transition: 'background-color 0.15s',
          }}
        >
          <SendIcon sx={{ fontSize: 24 }} aria-hidden />
        </IconButton>
        </Box>
        )}

        {/*
          User-recorded voice memo played back for the user themselves
          before they send it. There is no caption track to provide for
          a fresh in-memory blob the user just spoke into the mic.

          Always rendered (NOT gated on audioReady) so the browser can
          actually decode the blob and fire `canplay` - which is what
          flips audioReady true and swaps the loading shell for the
          full playback row.
        */}
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio
          ref={audioRef}
          src={reviewBlobUrl}
          // Force the browser to start decoding the blob immediately on
          // mount. Without this, the very first tap on Play raced the
          // initial decode and play() rejected silently.
          preload="auto"
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onCanPlayThrough={() => { /* readiness is timer-gated */ }}
          onLoadedMetadata={(e) => {
            const target = e.currentTarget;
            // Some browsers (notably Chrome) report Infinity for streamed
            // MediaRecorder blobs until we seek to the end. Fall back to
            // the elapsed counter we kept locally during recording.
            const fromMeta = target.duration;
            if (Number.isFinite(fromMeta) && fromMeta > 0) {
              setReviewDurationSec(fromMeta);
            }
          }}
          onTimeUpdate={(e) => setReviewCurrentSec(e.currentTarget.currentTime)}
          onEnded={() => {
            setIsPlaying(false);
            setReviewCurrentSec(reviewDurationSec);
          }}
        />
      </Box>
    );
  }

  // ── IDLE / ERROR / TRANSCRIBING IDLE BUTTON ──────────────────────────────
  return (
    <IconButton
      onClick={startRecording}
      disabled={disabled || recorderState === 'transcribing'}
      lang={locale}
      aria-label={t('voice.recordVoiceMessage')}
      title={
        recorderState === 'error'
          ? t('voice.micAccessDenied')
          : recorderState === 'transcribing'
            ? t('voice.transcribing')
            : t('voice.recordVoiceMessage')
      }
      sx={{
        height: 34,
        width: 34,
        transition: 'all 0.15s ease-in-out',
        '&:hover': {
          bgcolor: theme.palette.action.hover,
        },
      }}
    >
      <MicIcon
        aria-hidden
        sx={{ fontSize: 20 }}
        color={
          disabled || recorderState === 'transcribing'
            ? 'disabled'
            : recorderState === 'error'
              ? 'error'
              : 'action'
        }
      />
    </IconButton>
  );
}
